import React, { useState, useEffect } from 'react';
import {
  getUserAvailability,
  getMyAvailability,
  updateAvailabilityStatus,
  clearAvailabilityStatus,
  addAvailabilityBlock,
  updateAvailabilityBlock,
  deleteAvailabilityBlock,
} from '../services/api';
import './AvailabilityTimeline.css';

const STATUS_OPTIONS = [
  { id: 'open_to_auditions', label: 'Open to Auditions', color: '#5a407d' },
  { id: 'currently_casting', label: 'Currently Casting', color: '#e32c74' },
  { id: 'available_for_hire', label: 'Available for Hire', color: '#f57b57' },
  { id: 'seeking_collaborators', label: 'Seeking Collaborators', color: '#f29d40' },
  { id: 'in_production', label: 'In Production', color: '#888' },
];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function AvailabilityBadge({ status }) {
  if (!status) return null;
  const opt = STATUS_OPTIONS.find((s) => s.id === status);
  if (!opt) return null;
  return (
    <span className="availability-badge" style={{ background: opt.color }}>
      {opt.label}
    </span>
  );
}

function AvailabilityTimeline({ userId, isOwnProfile }) {
  const [data, setData] = useState({
    status: null,
    details: null,
    location_pref: null,
    blocks: [],
  });
  const [editing, setEditing] = useState(false);
  const [addingBlock, setAddingBlock] = useState(false);
  const [editingBlockId, setEditingBlockId] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(null);
  const [dateError, setDateError] = useState('');
  const [newBlock, setNewBlock] = useState({
    block_type: 'booked',
    title: '',
    company: '',
    start_date: '',
    end_date: '',
    notes: '',
  });
  const [statusDetails, setStatusDetails] = useState('');
  const [locationPref, setLocationPref] = useState('');

  useEffect(() => {
    loadData();
  }, [userId]);

  const loadData = async () => {
    try {
      const res = isOwnProfile ? await getMyAvailability() : await getUserAvailability(userId);
      const d = res.data;
      setData(d);
      setStatusDetails(d.details || '');
      setLocationPref(d.location_pref || '');
    } catch (e) {
      console.error('Error loading availability:', e);
    }
  };

  const handleSetStatus = async (statusId) => {
    try {
      await updateAvailabilityStatus(statusId, statusDetails, locationPref);
      setData((prev) => ({ ...prev, status: statusId }));
      setEditing(false);
    } catch (e) {
      console.error('Error setting status:', e);
    }
  };

  const handleClearStatus = async () => {
    try {
      await clearAvailabilityStatus();
      setData((prev) => ({ ...prev, status: null, details: null, location_pref: null }));
      setEditing(false);
    } catch (e) {
      console.error('Error clearing status:', e);
    }
  };

  const resetForm = () => {
    setEditingBlockId(null);
    setAddingBlock(false);
    setDateError('');
    setNewBlock({
      block_type: 'booked',
      title: '',
      company: '',
      start_date: '',
      end_date: '',
      notes: '',
    });
  };

  const handleEditBlock = (block) => {
    setEditingBlockId(block.id);
    setAddingBlock(true);
    setDateError('');
    setNewBlock({
      block_type: block.block_type,
      title: block.title || '',
      company: block.company || '',
      start_date: block.start_date,
      end_date: block.end_date,
      notes: block.notes || '',
    });
  };

  const handleSubmitBlock = async () => {
    if (!newBlock.start_date || !newBlock.end_date) return;
    if (newBlock.end_date < newBlock.start_date) {
      setDateError('End date must be on or after start date.');
      return;
    }
    setDateError('');
    try {
      if (editingBlockId) {
        await updateAvailabilityBlock(editingBlockId, newBlock);
        setData((prev) => ({
          ...prev,
          blocks: prev.blocks
            .map((b) => (b.id === editingBlockId ? { ...b, ...newBlock } : b))
            .sort((a, b) => a.start_date.localeCompare(b.start_date)),
        }));
      } else {
        const res = await addAvailabilityBlock(newBlock);
        const block = res.data;
        setData((prev) => ({
          ...prev,
          blocks: [...prev.blocks, block].sort((a, b) =>
            a.start_date.localeCompare(b.start_date)
          ),
        }));
      }
      resetForm();
    } catch (e) {
      const apiErr = e.response?.data?.error;
      if (e.response?.status === 400 && apiErr) {
        setDateError(apiErr);
      } else {
        console.error('Error saving block:', e);
      }
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmingDelete) return;
    const id = confirmingDelete.id;
    try {
      await deleteAvailabilityBlock(id);
      setData((prev) => ({ ...prev, blocks: prev.blocks.filter((b) => b.id !== id) }));
      setConfirmingDelete(null);
    } catch (e) {
      console.error('Error deleting block:', e);
    }
  };

  // Build 12-month timeline starting from current month
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();

  const getBlockStyle = (block) => {
    const start = new Date(block.start_date);
    const end = new Date(block.end_date);
    const timelineStart = new Date(currentYear, currentMonth, 1);
    const timelineEnd = new Date(currentYear, currentMonth + 12, 0);

    const totalDays = (timelineEnd - timelineStart) / (1000 * 60 * 60 * 24);
    const startOffset = Math.max(0, (start - timelineStart) / (1000 * 60 * 60 * 24));
    const endOffset = Math.min(totalDays, (end - timelineStart) / (1000 * 60 * 60 * 24));

    if (endOffset <= 0 || startOffset >= totalDays) return null;

    return {
      left: `${(startOffset / totalDays) * 100}%`,
      width: `${((endOffset - startOffset) / totalDays) * 100}%`,
    };
  };

  const blockColors = {
    booked: '#333',
    auditioning: '#f29d40',
  };

  return (
    <div className="availability-section">
      {/* Status Badge */}
      <div className="availability-header">
        <div className="availability-header-left">
          {data.status ? (
            <AvailabilityBadge status={data.status} />
          ) : isOwnProfile ? (
            <button className="availability-set-status" onClick={() => setEditing(true)}>
              + Set your status
            </button>
          ) : null}
          {data.details && <span className="availability-details-text">{data.details}</span>}
        </div>
        {isOwnProfile && data.status && (
          <button className="availability-edit-btn" onClick={() => setEditing(true)}>
            Edit
          </button>
        )}
      </div>

      {/* Status Editor */}
      {editing && (
        <div className="availability-editor">
          <div className="availability-status-options">
            {STATUS_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                className={`availability-status-option ${data.status === opt.id ? 'active' : ''}`}
                style={
                  data.status === opt.id
                    ? { borderColor: opt.color, background: opt.color + '10' }
                    : {}
                }
                onClick={() => handleSetStatus(opt.id)}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <input
            type="text"
            className="availability-details-input"
            placeholder="Add details (e.g. 'Casting for Spring Awakening' or 'Available June-August')"
            value={statusDetails}
            onChange={(e) => setStatusDetails(e.target.value)}
          />
          <input
            type="text"
            className="availability-details-input"
            placeholder="Location preference (e.g. NYC, willing to travel)"
            value={locationPref}
            onChange={(e) => setLocationPref(e.target.value)}
          />
          <div className="availability-editor-actions">
            <button
              className="availability-save-btn"
              onClick={() => handleSetStatus(data.status || 'open_to_auditions')}
            >
              Save
            </button>
            {data.status && (
              <button className="availability-clear-btn" onClick={handleClearStatus}>
                Clear Status
              </button>
            )}
            <button className="availability-cancel-btn" onClick={() => setEditing(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Timeline */}
      {(data.blocks.length > 0 || isOwnProfile) && (
        <div className="availability-timeline">
          <div className="availability-timeline-header">
            <span className="availability-timeline-title">Availability</span>
            {isOwnProfile && (
              <button className="availability-add-block-btn" onClick={() => setAddingBlock(true)}>
                + Add
              </button>
            )}
          </div>

          {/* Month labels */}
          <div className="availability-months">
            {Array.from({ length: 12 }, (_, i) => {
              const monthIdx = (currentMonth + i) % 12;
              return (
                <span key={i} className="availability-month">
                  {MONTHS[monthIdx]}
                </span>
              );
            })}
          </div>

          {/* Timeline bar — green = available by default, dark = booked */}
          <div className="availability-bar available-default">
            {data.blocks
              .filter((b) => b.block_type !== 'available')
              .map((block) => {
                const style = getBlockStyle(block);
                if (!style) return null;
                return (
                  <div
                    key={block.id}
                    className={`availability-block ${block.block_type}`}
                    style={{ ...style, background: blockColors[block.block_type] || '#333' }}
                    title={`${block.title || block.block_type} (${block.start_date} – ${block.end_date})`}
                  >
                    {parseFloat(style.width) > 8 && <span>{block.title || block.block_type}</span>}
                  </div>
                );
              })}
          </div>

          {/* Legend */}
          <div className="availability-legend">
            <span className="availability-legend-item">
              <span className="availability-legend-dot" style={{ background: '#22c55e' }} />
              Available
            </span>
            <span className="availability-legend-item">
              <span className="availability-legend-dot" style={{ background: '#333' }} />
              Booked
            </span>
            <span className="availability-legend-item">
              <span className="availability-legend-dot" style={{ background: '#f29d40' }} />
              Auditioning
            </span>
          </div>

          {/* Block list */}
          {data.blocks.length > 0 && (
            <div className="availability-block-list">
              {data.blocks.map((block) => (
                <div key={block.id} className="availability-block-item">
                  <span
                    className="availability-block-dot"
                    style={{ background: blockColors[block.block_type] || '#999' }}
                  />
                  <div className="availability-block-info">
                    <strong>
                      {block.title ||
                        block.block_type.charAt(0).toUpperCase() + block.block_type.slice(1)}
                    </strong>
                    {block.company && <span> — {block.company}</span>}
                    <span className="availability-block-dates">
                      {' '}
                      · {block.start_date} to {block.end_date}
                    </span>
                  </div>
                  {isOwnProfile && (
                    <>
                      <button
                        className="availability-block-edit"
                        onClick={() => handleEditBlock(block)}
                        aria-label="Edit event"
                      >
                        Edit
                      </button>
                      <button
                        className="availability-block-delete"
                        onClick={() => setConfirmingDelete(block)}
                        aria-label="Delete event"
                      >
                        ×
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Block Form */}
      {addingBlock && (
        <div className="availability-add-form">
          <div className="availability-add-form-title">
            {editingBlockId ? 'Edit availability' : 'Add availability'}
          </div>
          <div className="availability-add-form-row">
            <select
              value={newBlock.block_type}
              onChange={(e) => setNewBlock((prev) => ({ ...prev, block_type: e.target.value }))}
            >
              <option value="booked">Booked</option>
              <option value="auditioning">Auditioning</option>
            </select>
            <input
              type="text"
              placeholder="Show/project name"
              value={newBlock.title}
              onChange={(e) => setNewBlock((prev) => ({ ...prev, title: e.target.value }))}
            />
            <input
              type="text"
              placeholder="Company/theater"
              value={newBlock.company}
              onChange={(e) => setNewBlock((prev) => ({ ...prev, company: e.target.value }))}
            />
          </div>
          <div className="availability-add-form-row">
            <input
              type="date"
              value={newBlock.start_date}
              onChange={(e) => setNewBlock((prev) => ({ ...prev, start_date: e.target.value }))}
            />
            <input
              type="date"
              value={newBlock.end_date}
              onChange={(e) => setNewBlock((prev) => ({ ...prev, end_date: e.target.value }))}
            />
            <button className="availability-add-submit" onClick={handleSubmitBlock}>
              {editingBlockId ? 'Update' : 'Add Block'}
            </button>
            <button className="availability-cancel-btn" onClick={resetForm}>
              Cancel
            </button>
          </div>
          {dateError && <div className="availability-date-error">{dateError}</div>}
        </div>
      )}

      {/* Delete Confirm Modal */}
      {confirmingDelete && (
        <div
          className="availability-confirm-overlay"
          onClick={() => setConfirmingDelete(null)}
          role="presentation"
        >
          <div
            className="availability-confirm-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="availability-confirm-title"
          >
            <div className="availability-confirm-title" id="availability-confirm-title">
              Delete this event?
            </div>
            <div className="availability-confirm-message">
              <strong>{confirmingDelete.title || confirmingDelete.block_type}</strong>
              <span className="availability-confirm-dates">
                {' '}
                · {confirmingDelete.start_date} to {confirmingDelete.end_date}
              </span>
            </div>
            <div className="availability-confirm-actions">
              <button
                className="availability-confirm-cancel"
                onClick={() => setConfirmingDelete(null)}
              >
                Cancel
              </button>
              <button
                className="availability-confirm-delete"
                onClick={handleConfirmDelete}
                autoFocus
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export { AvailabilityBadge };
export default AvailabilityTimeline;
